// =============================================================================
// FILE:
// frontend/src/modules/pms/material_logistics/pages/PackingDispatchWorkbenchPage.js
// =============================================================================

import React, {
  useEffect,
  useState
} from "react";

import {
  RefreshCw,
  Plus
} from "lucide-react";

import DispatchKPISection from "../components/DispatchKPISection";

import DispatchFilterPanel from "../components/DispatchFilterPanel";

import DispatchGrid from "../components/DispatchGrid";

import {
  getDispatchList
} from "../services/materialDispatchService";

import CreateDispatchDrawer from "../components/CreateDispatchDrawer";


// =============================================================================
// COMPONENT
// =============================================================================

export default function PackingDispatchWorkbenchPage() {

  // ===========================================================================
  // STATE
  // ===========================================================================
  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [filters, setFilters] =
    useState({

      status: "",

      search: ""
    });

  const [error, setError] =
    useState("");

  const [drawerOpen, setDrawerOpen] =
    useState(false);


  // ===========================================================================
  // LOAD DATA
  // ===========================================================================
  const loadDispatches =
    async () => {

      try {

        setLoading(true);

        setError("");

        const response =
          await getDispatchList(filters);

        console.log(
          "Dispatch Response:",
          response
        );

        if (response?.success) {

          setRows(
            response.data || []
          );

        } else {

          setRows([]);
        }

      } catch (err) {

        console.error(err);

        setError(
          "Failed to load dispatches"
        );

      } finally {

        setLoading(false);
      }
    };


  // ===========================================================================
  // INITIAL LOAD
  // ===========================================================================
  useEffect(() => {

    loadDispatches();

  }, []);


  // ===========================================================================
  // FILTER CHANGE
  // ===========================================================================
  const handleFilterChange = (
    field,
    value
  ) => {

    setFilters((prev) => ({

      ...prev,

      [field]: value
    }));
  };


  // ===========================================================================
  // APPLY FILTERS
  // ===========================================================================
  const handleApplyFilters = () => {

    loadDispatches();
  };


  // ===========================================================================
  // HANDLE DRAWER SUCCESS
  // ===========================================================================
  const handleDrawerSuccess = () => {

    setDrawerOpen(false);

    loadDispatches();
  };


  // ===========================================================================
  // KPI CALCULATION
  // ===========================================================================
  const kpis = {

    draft: rows.filter(
      (x) =>
        x.dispatch_status === "DRAFT"
    ).length,

    submitted: rows.filter(
      (x) =>
        x.dispatch_status ===
        "SUBMITTED"
    ).length,

    approved: rows.filter(
      (x) =>
        x.dispatch_status ===
        "APPROVED"
    ).length,

    totalBoxes: rows.reduce(
      (sum, x) =>
        sum +
        Number(
          x.total_boxes || 0
        ),
      0
    ),

    totalWeight: rows.reduce(
      (sum, x) =>
        sum +
        Number(
          x.total_weight || 0
        ),
      0
    )
  };


  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <div style={styles.page}>


      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}
      <div style={styles.headerCard}>


        <div>

          <div style={styles.headerTitle}>
            Packing & Dispatch
          </div>

          <div style={styles.headerSubtitle}>

            Execute logistics packing,
            dispatch and warehouse
            transfer operations

          </div>

        </div>


        <div style={styles.headerActions}>


          <button
            style={styles.refreshBtn}
            onClick={loadDispatches}
          >

            <RefreshCw size={16} />

            Refresh

          </button>


          <button
            style={styles.createBtn}
            onClick={() =>
              setDrawerOpen(true)
            }
          >

            <Plus size={16} />

            Create Packing List

          </button>

        </div>

      </div>


      {/* ================================================================ */}
      {/* KPI SECTION */}
      {/* ================================================================ */}
      <DispatchKPISection
        kpis={kpis}
      />


      {/* ================================================================ */}
      {/* FILTER SECTION */}
      {/* ================================================================ */}
      <DispatchFilterPanel

        filters={filters}

        onChange={
          handleFilterChange
        }

        onApply={
          handleApplyFilters
        }
      />


      {/* ================================================================ */}
      {/* ERROR */}
      {/* ================================================================ */}
      {

        error && (

          <div style={styles.errorBox}>

            {error}

          </div>
        )
      }


      {/* ================================================================ */}
      {/* GRID */}
      {/* ================================================================ */}
      <DispatchGrid

        rows={rows}

        loading={loading}
      />


      {/* ================================================================ */}
      {/* CREATE DISPATCH DRAWER */}
      {/* ================================================================ */}
      <CreateDispatchDrawer

        open={drawerOpen}

        onClose={() =>
          setDrawerOpen(false)
        }

        onSuccess={
          handleDrawerSuccess
        }
      />

    </div>
  );
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  page: {

    padding: 20,

    background: "#f5f7fb",

    minHeight: "100vh"
  },


  // ===========================================================================
  // HEADER
  // ===========================================================================
  headerCard: {

    background:
      "linear-gradient(90deg, #2563eb, #3b82f6)",

    borderRadius: 14,

    padding: "18px 22px",

    marginBottom: 20,

    color: "#fff",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center"
  },

  headerTitle: {

    fontSize: 24,

    fontWeight: 700
  },

  headerSubtitle: {

    fontSize: 13,

    opacity: 0.92,

    marginTop: 4
  },

  headerActions: {

    display: "flex",

    gap: 10
  },

  refreshBtn: {

    display: "flex",

    alignItems: "center",

    gap: 8,

    background: "#fff",

    color: "#2563eb",

    border: "none",

    borderRadius: 8,

    padding: "10px 16px",

    fontWeight: 600,

    cursor: "pointer"
  },

  createBtn: {

    display: "flex",

    alignItems: "center",

    gap: 8,

    background: "#111827",

    color: "#fff",

    border: "none",

    borderRadius: 8,

    padding: "10px 16px",

    fontWeight: 600,

    cursor: "pointer"
  },

  errorBox: {

    background: "#fee2e2",

    color: "#b91c1c",

    padding: 14,

    borderRadius: 10,

    marginBottom: 20,

    fontSize: 14,

    fontWeight: 500
  }
};